import { FormEvent, useEffect, useMemo, useState } from 'react';
import { formatLocalDay } from './lib/date';

type View = 'dashboard' | 'history' | 'trends';

const viewLabels: Record<View, string> = {
  dashboard: 'Today',
  history: 'History',
  trends: 'Trends',
};

type SessionUser = {
  id: string;
  email: string;
};

type AuthState = {
  authenticated: boolean;
  setupRequired: boolean;
  user?: SessionUser;
};

function App() {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [authError, setAuthError] = useState('');

  async function loadAuth() {
    try {
      const response = await fetch('/api/auth/me', { headers: { Accept: 'application/json' } });
      const body = await response.json() as AuthState;
      if (!response.ok) throw new Error('Unable to check the current session.');
      setAuthState(body);
      setAuthError('');
    } catch {
      setAuthError('We could not verify your session. Check your connection and try again.');
    }
  }

  useEffect(() => {
    // Authentication is an external request whose result initializes the UI state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadAuth();
  }, []);

  if (!authState) {
    return (
      <main className="auth-shell">
        <section className="auth-card" aria-live="polite">
          <div className="brand-lockup auth-brand">
            <div className="brand-mark" aria-hidden="true">G</div>
            <div>
              <p className="eyebrow">Personal tracker</p>
              <p className="brand-name">Grounded</p>
            </div>
          </div>
          <h1>Checking your private workspace.</h1>
          <p className="muted-copy">{authError || 'One moment while we verify your session.'}</p>
          {authError && <button className="primary-button auth-retry" type="button" onClick={() => void loadAuth()}>Try again</button>}
        </section>
      </main>
    );
  }

  if (!authState.authenticated) {
    return <AuthScreen setupRequired={authState.setupRequired} onAuthenticated={(user) => setAuthState({ authenticated: true, setupRequired: false, user })} />;
  }

  return <TrackerApp user={authState.user!} onLogout={() => setAuthState({ authenticated: false, setupRequired: false })} />;
}

function AuthScreen({ setupRequired, onAuthenticated }: { setupRequired: boolean; onAuthenticated: (user: SessionUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      const response = await fetch(setupRequired ? '/api/auth/register' : '/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await response.json() as { error?: string; user?: SessionUser };
      if (!response.ok || !body.user) {
        setNotice(body.error || 'Unable to complete sign in.');
        return;
      }
      onAuthenticated(body.user);
    } catch {
      setNotice('Unable to reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="brand-lockup auth-brand">
          <div className="brand-mark" aria-hidden="true">G</div>
          <div>
            <p className="eyebrow">Personal tracker</p>
            <p className="brand-name">Grounded</p>
          </div>
        </div>
        <p className="section-kicker">Private workspace</p>
        <h1>{setupRequired ? 'Create your account.' : 'Welcome back.'}</h1>
        <p className="muted-copy">
          {setupRequired ? 'Set up the first account to protect your nutrition and activity data.' : 'Sign in to continue to your private tracker.'}
        </p>
        <form className="auth-form" onSubmit={handleSubmit}>
          <label htmlFor="auth-email">Email</label>
          <input id="auth-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          <label htmlFor="auth-password">Password</label>
          <input id="auth-password" type="password" autoComplete={setupRequired ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} maxLength={128} required />
          {setupRequired && <p className="auth-helper">Use at least 12 characters. Additional user invitations can be added later.</p>}
          {notice && <p className="inline-notice" role="alert">{notice}</p>}
          <button className="primary-button auth-submit" type="submit" disabled={busy}>{busy ? 'Working…' : setupRequired ? 'Create account' : 'Sign in'} <span aria-hidden="true">→</span></button>
        </form>
        <p className="auth-security-note">Passwords are protected by the server and sessions use secure, http-only cookies.</p>
      </section>
    </main>
  );
}

function TrackerApp({ user, onLogout }: { user: SessionUser; onLogout: () => void }) {
  const [view, setView] = useState<View>('dashboard');
  const [foodText, setFoodText] = useState('');
  const [notice, setNotice] = useState('');
  const today = useMemo(() => formatLocalDay(), []);

  function handleReview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!foodText.trim()) {
      setNotice('Enter what you ate to begin.');
      return;
    }
    setNotice('Food parsing will be connected in the next implementation slice. Your text has not been saved.');
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">G</div>
          <div>
            <p className="eyebrow">Personal tracker</p>
            <p className="brand-name">Grounded</p>
          </div>
        </div>

        <nav className="main-nav" aria-label="Main navigation">
          {(Object.keys(viewLabels) as View[]).map((item) => (
            <button
              className={`nav-item ${view === item ? 'is-active' : ''}`}
              key={item}
              onClick={() => setView(item)}
              type="button"
            >
              <span className={`nav-icon nav-icon-${item}`} aria-hidden="true" />
              {viewLabels[item]}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="privacy-chip"><span className="status-dot" /> Private workspace</div>
          <p>Estimates are guides, not medical advice.</p>
        </div>
      </aside>

      <main className="main-content">
        <header className="page-header">
          <div>
            <p className="eyebrow">{today}</p>
            <h1>{view === 'dashboard' ? 'A clear view of today.' : viewLabels[view]}</h1>
          </div>
          <div className="account-actions">
            <span className="account-email" title={user.email}>{user.email}</span>
            <button className="avatar-button" type="button" onClick={onLogout} aria-label="Sign out">Exit</button>
          </div>
        </header>

        {view === 'dashboard' && (
          <Dashboard
            foodText={foodText}
            notice={notice}
            onFoodTextChange={setFoodText}
            onReview={handleReview}
          />
        )}
        {view === 'history' && <EmptyView title="Your food history will live here." detail="Saved meals will appear chronologically once the database and logging flow are connected." />}
        {view === 'trends' && <EmptyView title="Your trends will take shape here." detail="Weight, nutrition, and activity trends will appear after you have a few saved records." />}
      </main>
    </div>
  );
}

type DashboardProps = {
  foodText: string;
  notice: string;
  onFoodTextChange: (value: string) => void;
  onReview: (event: FormEvent<HTMLFormElement>) => void;
};

function Dashboard({ foodText, notice, onFoodTextChange, onReview }: DashboardProps) {
  return (
    <div className="dashboard-grid">
      <section className="welcome-panel panel-span-full">
        <div>
          <p className="section-kicker">Start with one honest entry</p>
          <h2>What did you eat?</h2>
          <p className="muted-copy">Use normal language. We’ll turn it into editable estimates before anything is saved.</p>
        </div>
        <div className="welcome-orb" aria-hidden="true"><span>+</span></div>
      </section>

      <section className="composer-card panel-span-two">
        <form onSubmit={onReview}>
          <label className="sr-only" htmlFor="food-entry">Describe your food</label>
          <textarea
            id="food-entry"
            value={foodText}
            onChange={(event) => onFoodTextChange(event.target.value)}
            placeholder="e.g. 2 chapati, a small bowl of dal and tea with milk"
            rows={5}
          />
          <div className="composer-footer">
            <span className="helper-text">You can enter a snack, meal, or whole day.</span>
            <button className="primary-button" type="submit">Review estimate <span aria-hidden="true">→</span></button>
          </div>
        </form>
        {notice && <p className="inline-notice" role="status">{notice}</p>}
      </section>

      <MetricCard label="Calories" value="—" detail="Set up your profile to begin" tone="orange" />
      <MetricCard label="Protein" value="—" detail="No entries yet" tone="green" />

      <section className="setup-card panel-span-two">
        <div className="setup-icon" aria-hidden="true">✦</div>
        <div>
          <p className="section-kicker">Next step</p>
          <h3>Set up your profile</h3>
          <p className="muted-copy">Add your height, weight, activity level, and goal to get personal targets.</p>
        </div>
        <button className="text-button" type="button">Open profile <span aria-hidden="true">↗</span></button>
      </section>

      <section className="empty-card panel-span-full">
        <div className="empty-card-icon" aria-hidden="true">○</div>
        <div>
          <h3>No activity or weight logged today</h3>
          <p className="muted-copy">Keep it simple. Add active calories or weight whenever you’re ready.</p>
        </div>
        <button className="secondary-button" type="button">Add activity</button>
      </section>
    </div>
  );
}

function MetricCard({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: 'orange' | 'green' }) {
  return (
    <section className={`metric-card ${tone}`}>
      <div className="metric-header"><span>{label}</span><span className="metric-glyph" aria-hidden="true">↗</span></div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </section>
  );
}

function EmptyView({ title, detail }: { title: string; detail: string }) {
  return (
    <section className="large-empty-state">
      <div className="empty-state-mark" aria-hidden="true">✦</div>
      <h2>{title}</h2>
      <p className="muted-copy">{detail}</p>
    </section>
  );
}

export default App;
